import { GenericObject, IResponse } from "@/types";
import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { getCookie } from "cookies-next";
import { CONSTANTS } from "../constants";

const maxRetries = 4;
let retries = 0;

const token: string = "";

const axiosApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
});

axiosApi.defaults.headers.common["Authorization"] = token;

axiosApi.interceptors.request.use(
  async (config) => {
    let accessToken;

    // Server-side: Use `req` and `res`
    if (typeof window === "undefined") {
      const { cookies } = await import("next/headers"); // Server-side cookies
      let getCookies = await cookies();
      accessToken = getCookies.get(CONSTANTS.JWT_COOKIE_NAME)?.value;
    } else {
      // Client-side
      accessToken = getCookie(CONSTANTS.JWT_COOKIE_NAME);
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error?.response);
  }
);

axiosApi.interceptors.response.use(
  (response: AxiosResponse) => {
    // You can add your custom response handling logic here
    // For example, you can log the response or perform some actions
    // before returning it

    return response;
  },
  (error: any) => {
    // Handle errors here if needed
    if (error?.response?.status == 401) {
    }

    if (error.code === "ECONNRESET" && retries < maxRetries) {
      retries++;

      return axiosApi(error.config); // Retry the request
    }

    return Promise.reject(error);
  }
);

export async function get<T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<IResponse> {
  return axiosApi
    .get(url, { ...config })
    .then((response: AxiosResponse<IResponse>) => response.data)
    .catch((error) => {
      console.error("GET request error:", error);
      return Promise.reject(error);
    });
}

export async function post<T>(
  url: string,
  data: GenericObject,
  config: AxiosRequestConfig = {}
): Promise<IResponse> {
  return axiosApi
    .post(url, data, { ...config })
    .then((response: AxiosResponse<IResponse>) => response.data)
    .catch((error) => {
      return Promise.reject(error?.response?.data);
    });
}

export async function postImage<T>(
  url: string,
  data: GenericObject,
  config: AxiosRequestConfig = {}
): Promise<IResponse> {
  return axiosApi
    .post(url, data, { ...config })
    .then((response: AxiosResponse<IResponse>) => response.data)
    .catch((error) => {
      console.error("POST (image) request error:", error);
      return Promise.reject(error);
    });
}

export async function put<T>(
  url: string,
  data: GenericObject,
  config: AxiosRequestConfig = {}
): Promise<IResponse> {
  return axiosApi
    .put(url, data, { ...config })
    .then((response: AxiosResponse<IResponse>) => response.data)
    .catch((error) => {
      console.error("PUT request error:", error);
      return Promise.reject(error);
    });
}

export async function del<T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<IResponse> {
  return axiosApi
    .delete(url, { ...config })
    .then((response: AxiosResponse<IResponse>) => response.data)
    .catch((error) => {
      console.error("DELETE request error:", error);
      return Promise.reject(error);
    });
}
